// src/App.js
import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ToastProvider } from './contexts/ToastContext';
import { useWebSocket } from './hooks/useWebSocket';
import Sidebar from './components/layout/Sidebar';
import Topbar from './components/layout/Topbar';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import Users from './pages/Users';
import Hosting from './pages/Hosting';
import Settings from './pages/Settings';
import Themes from './pages/Themes';
import ModuleManager from './pages/ModuleManager';
import Monitor from './pages/Monitor';
import Reports from './pages/Reports';
import CalendarPage from './pages/CalendarPage';
import Docker from './pages/Docker';
import Invoices from './pages/Invoices';
import Tickets from './pages/Tickets';
import Backups from './pages/Backups';
import DnsManager from './pages/DnsManager';
import EmailManager from './pages/EmailManager';
import SshManager from './pages/SshManager';
import CronJobs from './pages/CronJobs';
import Alerts from './pages/Alerts';
import './index.css';

function AppLayout() {
  const { user, loading } = useAuth();
  const [wsConnected, setWsConnected] = useState(false);
  const [notifCount, setNotifCount] = useState(0);

  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'AUTH_OK') setWsConnected(true);
    if (msg.type === 'NOTIFICATION_COUNT') setNotifCount(msg.count);
    if (msg.type === 'PONG') setWsConnected(true);
  }, []);

  useWebSocket(user ? handleWsMessage : null);

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" style={{ width: 36, height: 36, margin: '0 auto 16px', borderWidth: 3 }} />
          <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Se încarcă...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Topbar wsConnected={wsConnected} notifCount={notifCount} />
        <div className="page-content">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/users" element={<Users />} />
            <Route path="/hosting" element={<Hosting />} />
            <Route path="/monitor" element={<Monitor />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/docker" element={<Docker />} />
            <Route path="/invoices" element={<Invoices />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/backups" element={<Backups />} />
            <Route path="/dns" element={<DnsManager />} />
            <Route path="/email" element={<EmailManager />} />
            <Route path="/ssh" element={<SshManager />} />
            <Route path="/cron" element={<CronJobs />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/themes" element={<Themes />} />
            <Route path="/module-manager" element={<ModuleManager />} />
            <Route path="/login" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppLayout />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
